import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  IconButton,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';
import StyledSelect from './shared/StyledSelect';

type UnitCategory = 'length' | 'weight' | 'temperature' | 'data' | 'time';

interface UnitDefinition {
  name: string;
  toBase: (value: number) => number;
  fromBase: (value: number) => number;
}

const unitDefinitions: Record<UnitCategory, Record<string, UnitDefinition>> = {
  length: {
    mm: { name: 'Millimeters', toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    cm: { name: 'Centimeters', toBase: (v) => v / 100, fromBase: (v) => v * 100 },
    m: { name: 'Meters', toBase: (v) => v, fromBase: (v) => v },
    km: { name: 'Kilometers', toBase: (v) => v * 1000, fromBase: (v) => v / 1000 },
    in: { name: 'Inches', toBase: (v) => v * 0.0254, fromBase: (v) => v / 0.0254 },
    ft: { name: 'Feet', toBase: (v) => v * 0.3048, fromBase: (v) => v / 0.3048 },
    yd: { name: 'Yards', toBase: (v) => v * 0.9144, fromBase: (v) => v / 0.9144 },
    mi: { name: 'Miles', toBase: (v) => v * 1609.344, fromBase: (v) => v / 1609.344 },
  },
  weight: {
    mg: { name: 'Milligrams', toBase: (v) => v / 1000000, fromBase: (v) => v * 1000000 },
    g: { name: 'Grams', toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    kg: { name: 'Kilograms', toBase: (v) => v, fromBase: (v) => v },
    t: { name: 'Metric Tons', toBase: (v) => v * 1000, fromBase: (v) => v / 1000 },
    oz: { name: 'Ounces', toBase: (v) => v * 0.0283495, fromBase: (v) => v / 0.0283495 },
    lb: { name: 'Pounds', toBase: (v) => v * 0.453592, fromBase: (v) => v / 0.453592 },
    st: { name: 'Stones', toBase: (v) => v * 6.35029, fromBase: (v) => v / 6.35029 },
  },
  temperature: {
    c: { name: 'Celsius', toBase: (v) => v, fromBase: (v) => v },
    f: { name: 'Fahrenheit', toBase: (v) => (v - 32) * 5/9, fromBase: (v) => v * 9/5 + 32 },
    k: { name: 'Kelvin', toBase: (v) => v - 273.15, fromBase: (v) => v + 273.15 },
  },
  data: {
    b: { name: 'Bytes', toBase: (v) => v, fromBase: (v) => v },
    kb: { name: 'Kilobytes', toBase: (v) => v * 1024, fromBase: (v) => v / 1024 },
    mb: { name: 'Megabytes', toBase: (v) => v * 1024 * 1024, fromBase: (v) => v / (1024 * 1024) },
    gb: { name: 'Gigabytes', toBase: (v) => v * 1024 * 1024 * 1024, fromBase: (v) => v / (1024 * 1024 * 1024) },
    tb: { name: 'Terabytes', toBase: (v) => v * 1024 * 1024 * 1024 * 1024, fromBase: (v) => v / (1024 * 1024 * 1024 * 1024) },
  },
  time: {
    ms: { name: 'Milliseconds', toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    s: { name: 'Seconds', toBase: (v) => v, fromBase: (v) => v },
    min: { name: 'Minutes', toBase: (v) => v * 60, fromBase: (v) => v / 60 },
    h: { name: 'Hours', toBase: (v) => v * 3600, fromBase: (v) => v / 3600 },
    d: { name: 'Days', toBase: (v) => v * 86400, fromBase: (v) => v / 86400 },
    wk: { name: 'Weeks', toBase: (v) => v * 604800, fromBase: (v) => v / 604800 },
  },
};

const categoryLabels: Record<UnitCategory, string> = {
  length: 'Length',
  weight: 'Weight',
  temperature: 'Temperature',
  data: 'Data',
  time: 'Time',
};

const UnitConverterUtility: React.FC = () => {
  const theme = useTheme();
  const [category, setCategory] = useState<UnitCategory>(() =>
    (localStorage.getItem('unit-category') as UnitCategory) || 'length'
  );
  const [fromUnit, setFromUnit] = useState(() => localStorage.getItem('unit-from') || 'm');
  const [toUnit, setToUnit] = useState(() => localStorage.getItem('unit-to') || 'ft');
  const [inputValue, setInputValue] = useState(() => localStorage.getItem('unit-input') || '');

  React.useEffect(() => {
    localStorage.setItem('unit-category', category);
    localStorage.setItem('unit-from', fromUnit);
    localStorage.setItem('unit-to', toUnit);
    localStorage.setItem('unit-input', inputValue);
  }, [category, fromUnit, toUnit, inputValue]);

  const units = unitDefinitions[category];

  React.useEffect(() => {
    const unitKeys = Object.keys(units);
    if (!units[fromUnit]) setFromUnit(unitKeys[0]);
    if (!units[toUnit]) setToUnit(unitKeys[1] || unitKeys[0]);
  }, [category, units, fromUnit, toUnit]);

  const convertedValue = useMemo(() => {
    const num = parseFloat(inputValue);
    if (isNaN(num) || !units[fromUnit] || !units[toUnit]) return '';
    const baseValue = units[fromUnit].toBase(num);
    const result = units[toUnit].fromBase(baseValue);
    return result.toLocaleString(undefined, { maximumFractionDigits: 10 });
  }, [inputValue, fromUnit, toUnit, units]);

  const handleSwap = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
  };

  return (
    <Box>
      <UtilityHeader 
        title="Unit Converter" 
        description="Convert between different units of measurement instantly." 
      />

      <Box sx={{ mb: 4 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.65rem' }}>
          Category
        </Typography>
        <ToggleButtonGroup
          value={category}
          exclusive
          onChange={(_, newCategory) => newCategory && setCategory(newCategory)}
          size="small"
          sx={{ flexWrap: 'wrap', bgcolor: 'rgba(255,255,255,0.03)' }}
        >
          {(Object.keys(categoryLabels) as UnitCategory[]).map((cat) => (
            <ToggleButton key={cat} value={cat} sx={{ px: 2, py: 1 }}>
              {categoryLabels[cat]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={4} alignItems="center">
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={3}>
            <StyledTextArea
              label="From"
              type="number"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Enter value..."
            />
            <StyledSelect 
              label="Unit" 
              value={fromUnit} 
              onChange={(e) => setFromUnit(e.target.value as string)}
            >
              {Object.entries(units).map(([key, unit]) => (
                <MenuItem key={key} value={key}>
                  {unit.name} ({key})
                </MenuItem>
              ))}
            </StyledSelect>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 2 }} sx={{ textAlign: 'center' }}>
          <IconButton
            onClick={handleSwap}
            sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
              width: 56,
              height: 56,
              transform: { xs: 'rotate(90deg)', md: 'none' }
            }}
          >
            <SwapHoriz fontSize="large" />
          </IconButton>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={3}>
            <StyledTextArea
              label="To"
              accentColor="secondary"
              value={convertedValue}
              placeholder="Result..."
              InputProps={{
                readOnly: true,
                sx: { 
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '1.1rem',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  borderRadius: 3,
                  color: 'secondary.main',
                  fontWeight: 700
                }
              }}
            />
            <StyledSelect 
              label="Unit" 
              accentColor="secondary"
              value={toUnit} 
              onChange={(e) => setToUnit(e.target.value as string)}
            >
              {Object.entries(units).map(([key, unit]) => (
                <MenuItem key={key} value={key}>
                  {unit.name} ({key})
                </MenuItem>
              ))}
            </StyledSelect>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UnitConverterUtility;
